import { useMsal, } from '@azure/msal-react';
import { useState, useEffect } from "react";
import { CompanyAPI } from '../../api/company/company-api-factory';
import Comparer from "../../app/comparer";
import { validUKPostCode, notNullOrEmpty, validateField } from "../../utils/validators";
import { PropertyTypeSelector } from "../../components/properties/property-type-selector";
import { MetaDataTable } from '../../components/properties/metadata-table';
import { GoogleMap } from '../../components/map/map';
import { Panel, PanelBody, PanelHeader } from "../../components/panel/panel";
import { FloatingTextInput } from "../reusable-elements/floating-text-input";
import { LandlordSelector } from '../reusable-elements/landlord-selector';
import { useNavigate } from "react-router-dom";
import { googleMapsConfig } from '../../app';

import './styles.css';

export const PropertyDetailsTab = ({ propertyDocument, selectedProject, selectedPropertyType, handleSetPropertyType, handleSaveProperty }) => {

    const { instance, inProgress, accounts } = useMsal();
    const navigate = useNavigate();

    //const getCoordinates = doc => doc?.coordinates ?? doc?.property?.coordinates ?? null;

    const mapConfig = () => {

        const coordinates = propertyDocument.coordinates;
        const lat = coordinates?.latitude;
        const lng = coordinates?.longitude;

        if (typeof lat !== 'number' || typeof lng !== 'number') {
            return null;
        }

        return {
            className: "h-100 w-100",
            style: { height: '100vh', width: '100%' },
            apiKey: googleMapsConfig.apiKey,
            center: { lat, lng },
            zoom: 17
        };
    };

    const primaryImage = property => {
        const primary = property?.photoGallery?.find(p => p?.isPrimaryImage);
        return primary?.pathIncSASToken ? primary : null;
    }

    const [rerender, setRerender] = useState(false);
    const [validationErrors, setValidationErrors] = useState(null);

    const [originalState, setOriginalState] = useState({
        name: propertyDocument.property.name,
        address: {
            doorNumberOrName: propertyDocument.property.address.doorNumberOrName,
            roadName: propertyDocument.property.address.roadName,
            town: propertyDocument.property.address.town,
            postalCode: propertyDocument.property.address.postalCode,
        },
        landlordOrOwner: propertyDocument.property.landlordOrOwner,
        selectedProject: propertyDocument.property.selectedProject
    });

    const [state, setState] = useState({
        name: propertyDocument.property.name,
        address: {
            doorNumberOrName: propertyDocument.property.address.doorNumberOrName,
            roadName: propertyDocument.property.address.roadName,
            town: propertyDocument.property.address.town,
            postalCode: propertyDocument.property.address.postalCode,
        },
        landlordOrOwner: propertyDocument.property.landlordOrOwner,
        selectedProject: propertyDocument.property.selectedProject
    });

    const [companyData, setCompanyData] = useState(null);
    const [fetchInProgress, setFetchInProgress] = useState(false);
    const [shouldFetch, setShouldFetch] = useState(true);

    useEffect(() => {
        if (shouldFetch && !fetchInProgress) {

            const api = new CompanyAPI(instance, inProgress, accounts, true); // TODO: REVERT - testing localStorage

            setFetchInProgress(true);

            api.getAll()
                .then(companies => { setCompanyData(companies); })
                .catch(error => { console.log("An error occurred getting Company Data", error); })
                .finally(() => {
                    setShouldFetch(false);
                    setFetchInProgress(false);
                });
        }

    }, [shouldFetch, fetchInProgress, instance, inProgress, accounts]);

    // Update state when propertyDocument changes
    useEffect(() => {
        const newState = {
            name: propertyDocument.property.name,
            doorNumberOrName: propertyDocument.property.address.doorNumberOrName,
            roadName: propertyDocument.property.address.roadName,
            town: propertyDocument.property.address.town,
            postalCode: propertyDocument.property.address.postalCode,
            landlordOrOwner: propertyDocument.property.landlordOrOwner,
            landlordOrOwnerId: propertyDocument.property.landlordOrOwner.id,
            selectedProject: propertyDocument.property.selectedProject
        };
        setState(newState);
        setOriginalState(structuredClone(newState));
    }, [propertyDocument]);

    const getLandlords = () => {
        const landlordsOrOwners = (companyData ?? [])
            .flatMap(c => c?.landlordsOrOwners ?? [])
            .filter(Boolean)
            .sort((a, b) => (a?.displayName ?? '').localeCompare(b?.displayName ?? ''));
        return landlordsOrOwners;
    };

    const comparer = new Comparer();

    const canSave = () => {
        let result = hasChanges();
        result &&= validationErrors ? Object.keys(validationErrors).length === 0 : result;
        return result;
    };

    const hasChanges = () => {
        const equal = comparer.equals(state, originalState);
        return !equal;
    };

    const image = primaryImage(propertyDocument.property);

    const validateState = stateToValidate => {
        validateField(() => notNullOrEmpty(stateToValidate.name), () => stateToValidate.name, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.doorNumberOrName), () => stateToValidate.doorNumberOrName, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.roadName), () => stateToValidate.roadName, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.town), () => stateToValidate.town, setValidationErrors);
        validateField(() => notNullOrEmpty(stateToValidate.postalCode) && validUKPostCode(stateToValidate.postalCode), () => stateToValidate.postalCode, setValidationErrors);
    };

    const handleStateUpdates = updateMethod => {

        setState(current => {
            const newState = updateMethod(current);
            validateState(newState);
            return newState;
        });
        setRerender(!rerender);

    };

    const handleCancel = () => {
        setState(structuredClone(originalState));
        setOriginalState(structuredClone(originalState));
    };

    const saveChanges = async () => {
        try {

            const saveObject = {
                name: state.name,
                address: {
                    doorNumberOrName: state.doorNumberOrName,
                    roadName: state.roadName,
                    town: state.town,
                    postalCode: state.postalCode,
                },
                landlordOrOwner: state.landlordOrOwner,
                landlordOrOwnerId: state.landlordOrOwnerId,
                selectedProject: state.selectedProject
            };

            const data = await handleSaveProperty(saveObject);
            const newState = {
                name: data.property.name,
                address: {
                    doorNumberOrName: data.property.address.doorNumberOrName,
                    roadName: data.property.address.roadName,
                    town: data.property.address.town,
                    postalCode: data.property.address.postalCode,
                },
                landlordOrOwner: data.property.landlordOrOwner,
                landlordOrOwnerId: data.landlordOrOwnerId,
                selectedProject: data.property.selectedProject
            };

            setState(newState);
            setOriginalState(structuredClone(newState));
        } catch (error) {
            console.log("An error occurred saving property details.", error);
        }
    };

    const mapCfg = mapConfig();

    return (
        <div className="tab-pane fade active show" id="nav-pills-tab-details">
            <div className="row mb-3">
                <div className="col-xl-3" key={"Details"}>
                    <Panel
                        showReload={false}
                        showDelete={false}
                    >
                        <PanelHeader>Details</PanelHeader>
                        <PanelBody>
                            <div>
                                <div className="row mb-15px">
                                    <label className="form-label col-form-label col-md-3">Primary Photo</label>
                                    <div className={'gallery col-md-9'}>
                                        {image && (
                                            // ref={el => { setImportant(el, "width", `${image.dimensions.w}px`); setImportant(el, "height", `${image.dimensions.h}px`); }}
                                            <div className="image w-100">
                                                <div className="image-inner">
                                                    <img src={image.pathIncSASToken} alt={state.name} />
                                                    <p className="image-caption">
                                                        {image.caption}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        {!image && <>Please select main image from Photo Gallery</>}
                                    </div>
                                </div>

                                <div className="row mb-15px">
                                    <label className="form-label col-form-label col-md-4">Kanban Board</label>
                                    <div className="col-md-8 mt-7px">
                                        {state.selectedProject ? (
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 link-like-div text-primary"
                                                onClick={() => {
                                                    navigate(`/kanban/${state.selectedProject}`);
                                                }}
                                            >
                                                {selectedProject?.name || 'View Kanban Board'}
                                            </button>
                                        ) : (
                                            <span className="text-muted">No kanban board assigned</span>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    <label className="form-label col-form-label col-md-4">Property Type</label>
                                    <div className="col-md-8 mt-7px">
                                        <PropertyTypeSelector
                                            types={[{ name: "Single", typeId: 0 }, { name: "HMO", typeId: 1 }]} //TODO: move to an API - don't hard code!
                                            selectedType={selectedPropertyType}
                                            onTypeSelect={type => handleSetPropertyType(type)}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <label className="form-label col-form-label col-md-4 link-like-div" onClick={() => navigate(`/landlords/detail/${state.landlordOrOwner.id}`)}>
                                        Landlord
                                    </label>
                                    <div className="col-md-8 mt-7px">
                                        <LandlordSelector
                                            landlords={getLandlords()}
                                            selectedLandlord={state.landlordOrOwner}
                                            onLandlordSelect={newValue => { handleStateUpdates(v => { v.landlord = newValue; return v; }); }}
                                        />
                                    </div>
                                </div>

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.name ? "Enter" : ""} Name`}
                                    text={state.name}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.name = newValue; return v; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.name); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.doorNumberOrName ? "Enter" : ""} Door Number or Name`}
                                    text={state.doorNumberOrName}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.doorNumberOrName = newValue; return v; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.doorNumberOrName); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.roadName ? "Enter" : ""} Road Name`}
                                    text={state.roadName}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.roadName = newValue; return v; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.roadName); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.town ? "Enter" : ""} Town`}
                                    text={state.town}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.town = newValue; return v; }) }}
                                    required={true}
                                    validatorFn={() => { return notNullOrEmpty(state.town); }}
                                />

                                <FloatingTextInput
                                    className={"mt-1"}
                                    label={`${!state.postalCode ? "Enter" : ""} Post Code`}
                                    text={state.postalCode}
                                    updateCallback={newValue => { handleStateUpdates(v => { v.postalCode = newValue; return v; }) }}
                                    required={true}
                                    validatorFn={() => { return validUKPostCode(state.postalCode); }}
                                />

                                <div className='mt-10px'>
                                    {canSave() &&
                                        <button title='Save' className="btn btn-primary me-10px" onClick={saveChanges}>
                                            <i title='Save' className="fa fa-save"></i>&nbsp;&nbsp;Save
                                        </button>
                                    }
                                    {hasChanges() &&
                                        <button title='Cancel' className="btn btn-warning" onClick={handleCancel}>
                                            <i title='Cancel' className="fa fa-cancel"></i>&nbsp;&nbsp;Cancel
                                        </button>
                                    }
                                </div>

                                <MetaDataTable data={propertyDocument} />

                            </div>
                        </PanelBody>
                    </Panel>
                </div>
                <div className="col-xl-9" key={"Map"}>
                    <Panel
                        showReload={false}
                        showDelete={false}
                    >
                        <PanelHeader>Map</PanelHeader>
                        <PanelBody>
                            {mapCfg ? (
                                <GoogleMap
                                    {...mapCfg}
                                    markers={[{
                                        name: propertyDocument.property.name,
                                        navLink: `/property/detail/${propertyDocument.id}`,
                                        icon: 'fa fa-home',
                                        ...mapCfg
                                    }]}
                                />
                            ) : (
                                <div className="text-muted">
                                    Map unavailable until coordinates are set.
                                </div>
                            )}
                        </PanelBody>
                    </Panel>
                </div>
            </div>
        </div>


    );

}